import axios from 'axios';
import { api } from './api/api';

export type MonitoringEventType =
  | 'camada_acessada'
  | 'download'
  | 'geoserver'
  | 'filtro_aplicado'
  | 'dashboard_acessada'
  | 'filtro_dashboard_aplicado';

export type MonitoringFilterType = 'simples' | 'avancado';

type MonitoringEvent = {
  tipo_evento: MonitoringEventType;
  identificador_camada?: string;
  tipo_filtro?: MonitoringFilterType;
};

type MonitoringSessionLocation = {
  latitude?: number;
  longitude?: number;
};

type MonitoringResponse = {
  id_sessao: string;
};

type MonitoringSession = {
  id: string | null;
};

type FinalizeMonitoringSessionOptions = {
  useBeacon?: boolean;
};

const FINALIZE_SESSION_PATH = '/monitoramento/sessoes/finalizar/';
const monitoringApi = axios.create({
  baseURL: api.defaults.baseURL,
  withCredentials: true,
});

let monitoringQueue: Promise<void> = Promise.resolve();
let activeSession: MonitoringSession | null = null;

async function persistEvent(session: MonitoringSession, event: MonitoringEvent, retryWithoutSession = true): Promise<void> {
  const sessionId = session.id;
  try {
    const response = await monitoringApi.post<MonitoringResponse>('/monitoramento/eventos/', {
      ...event,
      ...(sessionId ? { id_sessao: sessionId } : {}),
    });
    session.id = response.data.id_sessao;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 409 && sessionId && retryWithoutSession) {
      session.id = null;
      await persistEvent(session, event, false);
      return;
    }
    throw error;
  }
}

async function persistSession(
  session: MonitoringSession,
  location: MonitoringSessionLocation,
  retryWithoutSession = true,
): Promise<void> {
  const sessionId = session.id;
  try {
    const response = await monitoringApi.post<MonitoringResponse>('/monitoramento/sessoes/', {
      ...location,
      ...(sessionId ? { id_sessao: sessionId } : {}),
    });
    session.id = response.data.id_sessao;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 409 && sessionId && retryWithoutSession) {
      session.id = null;
      await persistSession(session, location, false);
      return;
    }
    throw error;
  }
}

async function persistSessionFinalization(session: MonitoringSession): Promise<void> {
  if (!session.id) return;

  await monitoringApi.post(FINALIZE_SESSION_PATH, { id_sessao: session.id });
}

function enqueueMonitoringRequest(request: () => Promise<void>): Promise<void> {
  monitoringQueue = monitoringQueue
    .catch(() => undefined)
    .then(request)
    .catch(() => undefined);

  return monitoringQueue;
}

export function captureMonitoringEvent(event: MonitoringEvent): Promise<void> {
  const session = activeSession;
  if (!session) return Promise.resolve();

  return enqueueMonitoringRequest(() => persistEvent(session, event));
}

export function finalizeMonitoringSession(options: FinalizeMonitoringSessionOptions = {}): Promise<void> {
  const session = activeSession;
  activeSession = null;

  if (!session) return Promise.resolve();

  if (options.useBeacon && session.id && navigator.sendBeacon) {
    try {
      const baseURL = String(api.defaults.baseURL || '').replace(/\/$/, '');
      const body = new URLSearchParams({ id_sessao: session.id });
      if (navigator.sendBeacon(`${baseURL}${FINALIZE_SESSION_PATH}`, body)) {
        return Promise.resolve();
      }
    } catch {
      // Fall back to the regular queued request below.
    }
  }

  return enqueueMonitoringRequest(() => persistSessionFinalization(session));
}

export function initializeMonitoringSession(): Promise<void> {
  const previousSession = activeSession;
  const session: MonitoringSession = { id: null };
  activeSession = session;

  if (previousSession) {
    void enqueueMonitoringRequest(() => persistSessionFinalization(previousSession));
  }

  const initialization = enqueueMonitoringRequest(() => persistSession(session, {}));

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      position => {
        if (activeSession !== session) return;

        void enqueueMonitoringRequest(() =>
          activeSession === session
            ? persistSession(session, {
                latitude: Number(position.coords.latitude.toFixed(6)),
                longitude: Number(position.coords.longitude.toFixed(6)),
              })
            : Promise.resolve(),
        );
      },
      () => undefined,
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 5 * 60 * 1000 },
    );
  }

  return initialization;
}
